import React from "react";
import {RouteComponentProps} from "../../routers/withRouterProps";
import {UserStatusEnum} from "../../utils/types/user";
import {FormCard, Text, theme} from "@reside/ui";

import {DocumentTitle} from "../../atoms/document-title";
import {AdminLayout} from "../../atoms/admin-layout";
import {AdminPageTitle} from "../../atoms/admin-layout/AdminPageTitle";
import {UserFormHeader} from "./atoms/UserFormHeader";
import {UserFormik} from "./atoms/UserFormik";
import {useUserQuery} from "./hooks/useUserQuery";
import {Spinner} from "../../atoms/spinner";
import {PageAdmin404} from "../page-admin-404";
import {DeleteUserButton} from "./atoms/DeleteUserButton";
import {ResetPasswordButton} from "../../atoms/reset-password-button";
import {ResendActivationEmailButton} from "../../atoms/resend-activation-email-button";
import {getInitialValues} from "./helpers";
import {useEditUser} from "./hooks/useEditUser";
import {SSOBadge} from "../../atoms/list-table/SSOBadge";
import {withRouterProps} from "../../routers/withRouterProps";

type Props = RouteComponentProps<{userId: string}>;

export const PageAdminUserEdit = withRouterProps(({match, history}: Props) => {
  const {userId} = match.params;

  const {data: user, isFetching: isFetchingUser} = useUserQuery({userId});

  const {mutate: updateUser, isLoading: isEditingUser} = useEditUser(
    user,
    history,
  );

  if (isFetchingUser) {
    return (
      <AdminLayout>
        <Spinner />
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <PageAdmin404 />
      </AdminLayout>
    );
  }

  return (
    <>
      <DocumentTitle
        title={`Reside - Admin - Edit User - ${user.firstName} ${user.lastName}`}
      />
      <AdminLayout>
        <UserFormik
          user={user}
          initialValues={getInitialValues(user)}
          onSubmit={updateUser}
          userFormHeader={
            <UserFormHeader
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: theme.space[5],
                  }}
                >
                  <AdminPageTitle>Edit User</AdminPageTitle>
                  {user.organization?.hasSsoConfig?.ssoEntryExists && (
                    <SSOBadge
                      label={
                        user.isSsoUser
                          ? "SSO user"
                          : user.organization?.hasSsoConfig?.ssoIsEnabled
                          ? "SSO pending"
                          : null
                      }
                    />
                  )}
                </div>
              }
              isSubmitting={isEditingUser}
            />
          }
          resetPasswordButton={
            user.status === UserStatusEnum.ACTIVE &&
            user.email &&
            !user?.organization?.hasSsoConfig?.ssoIsEnabled && (
              <ResetPasswordButton email={user.email} />
            )
          }
          resendActivationEmailButton={
            user.status === UserStatusEnum.NEW && (
              <ResendActivationEmailButton userId={user.id} />
            )
          }
          deleteUserButton={
            user.status !== UserStatusEnum.DELETED && (
              <FormCard title="Delete user">
                <Text>This action can&apos;t be undone.</Text>
                <DeleteUserButton />
              </FormCard>
            )
          }
        />
      </AdminLayout>
    </>
  );
});
